import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarCheck, CreditCard, Timer, XCircle, Sparkles } from "lucide-react";
import DashboardMockup from "./DashboardMockup";
import { useDemoModal } from "./DemoModal";

const lineVariants = {
    hidden: { y: "115%" },
    visible: (i) => ({
        y: 0,
        transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
    }),
};

const Hero = () => {
    const { openDemo } = useDemoModal();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const dashY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const dashRotate = useTransform(scrollYProgress, [0, 1], [0, 2.5]);

    return (
        <section
            ref={ref}
            data-testid="hero-section-container"
            className="relative overflow-hidden bg-gradient-to-b from-[#F7FEF9] via-white to-white pt-[68px]"
        >
            <div className="bg-grid-faint pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
            <div className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-[#16A34A]/[0.07] blur-3xl" />

            <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-[1.05fr_1.2fr] lg:gap-10 lg:px-8 lg:pb-28 lg:pt-20">
                {/* Left */}
                <div className="min-w-0">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        <span
                            data-testid="hero-badge-tag"
                            className="inline-flex items-center gap-2 rounded-full border border-[#16A34A]/20 bg-white px-4 py-1.5 text-xs font-semibold text-[#15803D] shadow-sm"
                        >
                            <Sparkles className="h-3.5 w-3.5 text-[#16A34A]" />
                            AI-Powered CRM for Growing Businesses
                        </span>
                    </motion.div>

                    <h1
                        data-testid="hero-headline-title"
                        className="mt-6 font-display text-[42px] font-extrabold leading-[1.08] tracking-tight text-[#111827] sm:text-5xl lg:text-[60px]"
                    >
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block" custom={0} variants={lineVariants} initial="hidden" animate="visible">
                                The CRM built for
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span className="block text-[#16A34A]" custom={1} variants={lineVariants} initial="hidden" animate="visible">
                                growing sales teams.
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        data-testid="hero-subheading-text"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg"
                    >
                        Capture leads, automate follow-ups, engage customers across
                        channels, and close more deals with Kryvo CRM.{" "}
                        <span className="font-semibold text-[#111827]">
                            Simple today. Powerful tomorrow.
                        </span>
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.62, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-8 flex flex-wrap items-center gap-3.5"
                    >
                        <Link
                            to="/signup"
                            data-testid="hero-btn-start-free"
                            className="group flex items-center gap-2 rounded-full bg-[#16A34A] px-7 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-[#16A34A]/25 transition-all hover:-translate-y-0.5 hover:bg-[#15803D] hover:shadow-xl hover:shadow-[#16A34A]/30"
                        >
                            Start Free
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                        <button
                            onClick={openDemo}
                            data-testid="hero-btn-book-demo"
                            className="group flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-7 py-3.5 text-[15px] font-semibold text-[#111827] shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#16A34A]/40 hover:text-[#15803D] hover:shadow-md"
                        >
                            <CalendarCheck className="h-4 w-4 text-[#16A34A]" />
                            Book a Demo
                        </button>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                        className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] font-medium text-[#64748B]"
                    >
                        <li className="flex items-center gap-1.5">
                            <CreditCard className="h-3.5 w-3.5 text-[#16A34A]" /> No credit card required
                        </li>
                        <li className="flex items-center gap-1.5">
                            <Timer className="h-3.5 w-3.5 text-[#16A34A]" /> 14-day free trial
                        </li>
                        <li className="flex items-center gap-1.5">
                            <XCircle className="h-3.5 w-3.5 text-[#16A34A]" /> Cancel anytime
                        </li>
                    </motion.ul>
                </div>

                {/* Right — dashboard with parallax + float */}
                <motion.div
                    initial={{ opacity: 0, y: 44, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    style={{ y: dashY, rotate: dashRotate }}
                    className="relative min-w-0 [perspective:1200px]"
                >
                    <div className="animate-floaty">
                        <DashboardMockup />
                    </div>
                    {/* Floating accent cards */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.15, duration: 0.7 }}
                        className="absolute -left-3 top-16 hidden rounded-xl border border-[#E5E7EB] bg-white/95 px-4 py-3 shadow-xl backdrop-blur md:block lg:-left-10"
                    >
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">New lead assigned</p>
                        <p className="mt-0.5 text-[13px] font-bold text-[#111827]">Skyline Group · ₹12.5L</p>
                        <p className="text-[10px] font-medium text-[#16A34A]">Auto-assigned to Rahul S.</p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.3, duration: 0.7 }}
                        className="absolute -right-2 bottom-10 hidden rounded-xl border border-[#E5E7EB] bg-white/95 px-4 py-3 shadow-xl backdrop-blur md:block lg:-right-6"
                    >
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">Follow-up automated</p>
                        <p className="mt-0.5 text-[13px] font-bold text-[#111827]">WhatsApp reminder sent</p>
                        <p className="text-[10px] font-medium text-[#16A34A]">Workflow #4 · On time</p>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
